import type { APIDesignerStore } from '..';
import type { PartialSchema } from '@ui/APIDesigner/TypeMenu';
import type { WritableDraft } from 'immer/dist/internal.js';
import type { RequestBodyObject, ResponseObject } from 'oas/types';
import type { OpenAPIV3 } from 'openapi-types';

import { safeClone } from '@readme/backend/iso';
import { getParameterContentType } from 'oas/utils';

import { moveParameterInObject, convertToType, getValidatedAPIDesignerState, generateUniqueName } from '../util';

const schemaPropertyMappingPattern = /0(-\d+)+/g;

type EnumValue = NonNullable<OpenAPIV3.SchemaObject['enum']>[number];
type EnumAction = { action: 'clear' } | { action: 'push'; value: EnumValue } | { action: 'remove'; value: number };

export const PRIMITIVE_TYPES = ['string', 'number', 'integer', 'boolean'];

/**
 * Represents the position of a parameter within a parent container.
 * Used for tracking parameter locations and performing move operations.
 *
 * @template ChildId - The type of the parameter ID (typically number or string)
 * @template ParentId - The type of the parent container ID (typically string)
 */
export interface ParamPosition<ChildId = number | string, ParentId = string> {
  /** The unique identifier of the parameter */
  parameterId: ChildId;
  /** The unique identifier of the parent container that holds this parameter */
  parentId: ParentId;
  /** The zero-based index position of the parameter within its parent container */
  position: number;
}

/**
 * Represents a move operation for reordering parameters.
 * Contains both the source and target positions for the parameter being moved.
 */
export interface MoveAction {
  /** The current position of the parameter being moved */
  source: ParamPosition;
  /** The target position where the parameter should be moved to */
  target: ParamPosition;
}

export type UpdateSchemaAttrObject =
  | { attribute: 'default'; value: NonNullable<OpenAPIV3.SchemaObject['default']> }
  | { attribute: 'description'; value: NonNullable<OpenAPIV3.SchemaObject['description']> }
  | { attribute: 'enum'; value: EnumAction }
  | { attribute: 'example'; value: string }
  | { attribute: 'move'; value: MoveAction }
  | { attribute: 'name'; value: string }
  | { attribute: 'required'; value: boolean }
  | { attribute: 'type'; value: PartialSchema };

type TraverseAction =
  | {
      action: 'add' | 'remove';
    }
  | (UpdateSchemaAttrObject & { action: 'update' });

// Used to determine which param object in the parameters array the target is
interface ParamInfo {
  index: number;
  location: Exclude<OpenAPIV3.ParameterObject['in'], 'cookie'>;
}

export type SchemaEditorTarget = 'request' | 'response' | (ParamInfo & { target: 'query' });

export interface ValidRequestSchema {
  mimeType: MIMETypeValue;
  refNames: string[];
  schema?: OpenAPIV3.SchemaObject;
  unsupportedFeatures: string[];
}

export interface ValidResponseSchema {
  description: string;
  refNames: string[];
  schemas: {
    type: MIMETypeValue;
    schema: OpenAPIV3.SchemaObject;
  }[];
  statusCode: string;
  unsupportedFeatures: string[];
}

/**
 * Recursively collects all $ref names from a schema, including nested
 * properties, array items, and polymorphic compositions (allOf/oneOf/anyOf).
 */
export function collectRefNames(
  schema: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject | undefined,
  visited = new Set<string>(),
): string[] {
  if (!schema) return [];

  if ('$ref' in schema) {
    const ref = schema.$ref;
    const name = ref.includes('/') ? ref.split('/').pop()! : ref;
    if (visited.has(name)) return [];
    visited.add(name);
    return [name];
  }

  const refs: string[] = [];
  const s = schema as OpenAPIV3.SchemaObject & { items?: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject };

  if (s.properties) {
    for (const prop of Object.values(s.properties)) {
      refs.push(...collectRefNames(prop as OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject, visited));
    }
  }

  if (s.items) {
    refs.push(...collectRefNames(s.items, visited));
  }

  for (const key of ['allOf', 'oneOf', 'anyOf'] as const) {
    if (s[key]) {
      for (const sub of s[key] as (OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject)[]) {
        refs.push(...collectRefNames(sub, visited));
      }
    }
  }

  if (s.additionalProperties && typeof s.additionalProperties === 'object') {
    refs.push(
      ...collectRefNames(s.additionalProperties as OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject, visited),
    );
  }

  return [...new Set(refs)];
}

/**
 * Recursively scans a schema for OAS features not yet supported by the API Designer.
 * Returns a deduplicated list of human-readable feature names found (e.g. "additional properties", "discriminator", "xml").
 */
export function collectUnsupportedFeatures(
  schema: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject | undefined,
): string[] {
  if (!schema || '$ref' in schema) return [];

  const features = new Set<string>();
  const visited = new WeakSet<object>();

  function walk(s: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject | undefined) {
    if (!s || '$ref' in s || visited.has(s)) return;
    visited.add(s);

    const obj = s as OpenAPIV3.SchemaObject & {
      xml?: unknown;
      discriminator?: unknown;
      items?: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject;
    };

    if (obj.additionalProperties && typeof obj.additionalProperties === 'object') {
      features.add('additional properties');
      walk(obj.additionalProperties as OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject);
    }

    if (obj.discriminator) {
      features.add('discriminator');
    }

    if (obj.xml) {
      features.add('xml');
    }

    if (obj.properties) {
      for (const prop of Object.values(obj.properties)) {
        walk(prop as OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject);
      }
    }

    if (obj.items) {
      walk(obj.items as OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject);
    }

    for (const key of ['allOf', 'oneOf', 'anyOf'] as const) {
      if (obj[key]) {
        for (const sub of obj[key] as (OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject)[]) {
          walk(sub);
        }
      }
    }
  }

  walk(schema);
  return [...features];
}

/**
 * MIME types that are supported in API Designer.
 *
 * This is used to validate the media type of a [media type object] (https://spec.openapis.org/oas/v3.1.0.html#media-type-object).
 * By default, the schemas for all MIME types are the same so we should in theory be able to edit all of them.
 * However, we currently only support editing one MIME type at a time.
 */
export const MIMEType = {
  // Structured data formats
  ApplicationCSV: 'application/csv',
  ApplicationJSON: 'application/json',
  ApplicationOctetStream: 'application/octet-stream',
  ApplicationFormURLEncoded: 'application/x-www-form-urlencoded',
  ApplicationXML: 'application/xml',

  // Multipart/form
  MultipartFormData: 'multipart/form-data',

  // Text formats
  TextCSV: 'text/csv',
  TextHTML: 'text/html',
  TextPlain: 'text/plain',
  TextXML: 'text/xml',
} as const;

export type MIMETypeValue = (typeof MIMEType)[keyof typeof MIMEType];

export const isTextBasedMIMEType = (mimeType: string): boolean => {
  return mimeType.startsWith('text/');
};

export const isStreamBasedMIMEType = (mimeType: MIMETypeValue): boolean => {
  return mimeType === MIMEType.ApplicationOctetStream;
};

export const isStatusCode = (value: string): boolean => /^\d{3}$/.test(value);
export const isPropertyPath = (value: string): boolean => /^\d+(-\d+)*$/.test(value);

/**
 * Wrapper functions around the type guards to throw an error if the value is invalid.
 */
export const validateStatusCode = (statusCode: string) => {
  if (statusCode && !isStatusCode(statusCode))
    throw new Error(`Invalid status code '${statusCode}'. Status codes must be 3-digit numbers (e.g., '200', '404').`);
};

export const validatePropertyPath = (propertyPath: string) => {
  if (propertyPath && !isPropertyPath(propertyPath))
    throw new Error(
      `Invalid property path '${propertyPath}'. Property paths must use dash-separated indices (e.g., '0', '0-1', '1-1-2').`,
    );
};

/**
 * Generates a unique property name that doesn't conflict with existing properties.
 * Uses the pattern 'name', 'name1', 'name2', etc.
 */
export const generateUniquePropertyName = (existingProperties: Record<string, unknown>): string => {
  return generateUniqueName('name', existingProperties);
};

/**
 * Helper function to traverse the schema object and modify it based on the provided `propertyPath` and `action`.
 * This function works with request bodies, response bodies, and query parameters.
 *
 * @param propertyPath The path to the property to modify. This is a string that represents the path to the property in the schema.
 * The first element is always 0, which represents the top-level object.
 * If the propertyPath is "0", it means the entire request body should be removed (for request targets).
 * If the propertyPath is "0-1-2", it means the property should be removed from the third property of the second property of the root schema object.
 * @param schema The schema object to modify.
 * @param action The action to perform on the schema. This can be one of the following:
 * - `add`: Adds a new property to the schema.
 * - `remove`: Removes a property from the schema.
 * - `update`: Updates an attribute of the schema to a provided value.
 */
function traverseAndModifySchema(
  action: TraverseAction,
  propertyPath: string,
  schema: WritableDraft<OpenAPIV3.SchemaObject>,
) {
  if (!propertyPath.match(schemaPropertyMappingPattern)) {
    return;
  }

  // if the id is not 0, this means the id contains a map to a sub-property
  const propertyIndexMap = propertyPath.split('-').map(Number);
  // we strip away the first element since it's always 0, which represents the top-level object.
  propertyIndexMap.splice(0, 1);

  /**
   * Keeps track of the current nested level of the object being traversed.
   */
  let currentNestedLevel = 0;
  /**
   * Represents the total number of nested levels that were defined in `propertyIndexMap`.
   */
  const totalNestedLevels = propertyIndexMap.length;
  /**
   * The object to traverse. This is the current schema object schema.
   *
   * As we traverse the object and its sub-properties, we update this variable to point to the current object.
   */
  let objectToTraverse: WritableDraft<OpenAPIV3.SchemaObject> = schema || {};

  /**
   * This is needed for query params because the starting position might be in an array of objects, and the properties are under 'items'
   * whereas in a request body, the root is always an object
   */
  if (objectToTraverse.type === 'array' && objectToTraverse.items) {
    // Handle both arrays of objects and arrays of primitives
    if ('properties' in objectToTraverse.items) {
      // Array of objects - traverse to the items object
      objectToTraverse = objectToTraverse.items;
    } else {
      // Array of primitives - we need to handle this case differently
      if (propertyIndexMap.length === 1) {
        const { attribute, value } = action as UpdateSchemaAttrObject & { action: 'update' };
        if (action.action === 'update' && objectToTraverse.items && !('$ref' in objectToTraverse.items)) {
          switch (attribute) {
            case 'description':
              objectToTraverse.items.description = value;
              break;
            case 'type':
              if (typeof value === 'object' && 'type' in value) {
                Object.assign(objectToTraverse.items, value);
              }
              objectToTraverse.items.format = value?.format || '';
              break;
            default:
              break;
          }
        }
        return; // Exit early since we've handled the primitive array case
      }
      throw new Error(`Cannot traverse primitive array items with property path: ${propertyIndexMap}`);
    }
  }

  /**
   * Traverse the object to find the property to add.
   * `propertyIndexMap` is an array of indices that represent the path to the property.
   *
   * For example, if the propertyIndexMap is `[1, 2]`, it means the the object to add a property to is
   * the 3rd property of the 2nd property of the root schema object.
   */
  propertyIndexMap.some((propertyIndex, i) => {
    const currentPropertyName = Object.keys(objectToTraverse.properties || {})[propertyIndex];

    if (typeof currentPropertyName !== 'string') {
      // handling in case the property index map contains invalid data
      return false;
    }

    const schemaToUpdate = objectToTraverse.properties?.[currentPropertyName];
    // This if-statement checks that we've reached the last nested level defined in `propertyIndexMap`
    // and that the current nested level is the same as the one we're on.
    // If so, we've found the object to make changes to to.
    if (i >= totalNestedLevels - 1 && i === currentNestedLevel) {
      switch (action.action) {
        // we're adding a new property to the schema
        case 'add':
          if (schemaToUpdate && 'properties' in schemaToUpdate) {
            const existingProperties = schemaToUpdate.properties || {};
            const newPropertyName = generateUniquePropertyName(existingProperties);
            schemaToUpdate.properties = {
              ...existingProperties,
              [newPropertyName]: { type: 'string' },
            };
          } else if (schemaToUpdate && 'items' in schemaToUpdate && 'properties' in schemaToUpdate.items) {
            // if the schemaToUpdate is an array of objects, we need to add the new property to the items object
            const existingProperties = schemaToUpdate.items.properties || {};
            const newPropertyName = generateUniquePropertyName(existingProperties);
            schemaToUpdate.items.properties = {
              ...existingProperties,
              [newPropertyName]: { type: 'string' },
            };
          }
          break;
        // we're removing a property from the schema
        case 'remove':
          if (objectToTraverse.properties) {
            delete objectToTraverse.properties[currentPropertyName];
          }

          // In addition to removing the property, we also need to remove it from the required array
          // (if it's in there).
          if (objectToTraverse.required) {
            const requiredIndex = objectToTraverse.required.indexOf(currentPropertyName);
            if (requiredIndex !== -1) {
              objectToTraverse.required.splice(requiredIndex, 1);
            }

            // If the required array is now empty, remove it entirely
            if (objectToTraverse.required.length === 0) {
              delete objectToTraverse.required;
            }
          }
          break;
        // we're updating an attribute of the schema
        case 'update':
          // eslint-disable-next-line no-case-declarations
          const { attribute, value } = action;
          // we're updating a attribute of the schema
          if (
            // these two conditions are to ensure that we don't try to update a reference object
            // (which is unlikely, but this is a nice type guard)
            schemaToUpdate &&
            !('$ref' in schemaToUpdate)
          ) {
            switch (attribute) {
              case 'default':
                schemaToUpdate.default = value;
                break;
              case 'description':
                schemaToUpdate.description = value;
                break;
              case 'enum':
                switch (value.action) {
                  case 'push':
                    if (!schemaToUpdate.enum) {
                      schemaToUpdate.enum = [];
                    }
                    if (!schemaToUpdate.enum.includes(value.value)) {
                      schemaToUpdate.enum.push(value.value);
                    }
                    break;
                  case 'remove':
                    if (!schemaToUpdate.enum || value.value < 0 || value.value >= schemaToUpdate.enum.length) {
                      throw new Error(`Enum at index ${value.value} does not exist`);
                    }
                    schemaToUpdate.enum.splice(value.value, 1);
                    if (schemaToUpdate.enum.length === 0) {
                      delete schemaToUpdate.enum;
                    }
                    break;
                  case 'clear':
                    delete schemaToUpdate.enum;
                    break;
                  default:
                    throw new Error('Invalid enum action');
                }
                break;
              case 'move':
                moveParameterInObject(objectToTraverse.properties || {}, action.value);
                break;
              case 'name':
                if (objectToTraverse.properties) {
                  // This is a bit cumbersome, but rather than adding a new property to the object
                  // and deleting the old one, we recreate the entire object one key at a time,
                  // replacing the old property name with the new one. This is to ensure that
                  // the order of the properties is preserved.
                  objectToTraverse.properties = Object.keys(objectToTraverse.properties).reduce<
                    typeof objectToTraverse.properties
                  >((prev, curr) => {
                    if (curr === currentPropertyName) {
                      prev[value] = objectToTraverse.properties![curr];
                    } else {
                      prev[curr] = objectToTraverse.properties![curr];
                    }
                    return prev;
                  }, {});

                  // if the surrounding object has an array of required fields, we need to
                  // make sure it reflects the new property name
                  if (objectToTraverse.required) {
                    // check if the current property name is in the required array
                    // and if it is, we need to update it to the new property name
                    const requiredIndex = objectToTraverse.required.indexOf(currentPropertyName);
                    if (requiredIndex !== -1) {
                      objectToTraverse.required[requiredIndex] = value;
                    }

                    // remove any duplicates of the old and new property names, if they exist
                    objectToTraverse.required = objectToTraverse.required.filter(
                      (item, index) => index === requiredIndex || item !== currentPropertyName || item !== value,
                    );
                  }
                }
                break;
              case 'required':
                // if the property is required, we need to update the required array.
                // first, we check if the required array exists
                if (objectToTraverse.required) {
                  // we need to remove the property from the required array if it exists
                  if (!value) {
                    objectToTraverse.required = objectToTraverse.required.filter(prop => prop !== currentPropertyName);
                    if (!objectToTraverse.required.length) {
                      // if the required array is empty, we should delete it
                      delete objectToTraverse.required;
                    }
                  } else if (value) {
                    const requiredIndex = objectToTraverse.required.indexOf(currentPropertyName);
                    // the required array doesn't contain the property name and we need to add it
                    if (requiredIndex === -1) {
                      objectToTraverse.required.push(currentPropertyName);
                    }
                  }
                } else if (value) {
                  // if the required array doesn't exist, we need to create it
                  objectToTraverse.required = [currentPropertyName];
                }
                break;
              case 'type':
                // eslint-disable-next-line no-case-declarations
                const previousType = schemaToUpdate.type;
                schemaToUpdate.type = value.type;

                // set a format if it exists, otherwise delete it
                if (value.format) {
                  schemaToUpdate.format = value.format;
                } else {
                  delete schemaToUpdate.format;
                }

                // if the type is an object and there are no properties, create an empty properties object
                // otherwise, remove the `properties` and `required` fields
                if (value.type === 'object') {
                  if (!schemaToUpdate.properties) schemaToUpdate.properties = {};
                } else {
                  delete schemaToUpdate.properties;
                  delete schemaToUpdate.required;
                }

                // if the type is an array, set the items field
                // otherwise, remove the items field
                if (value.type === 'array' && value.items) {
                  (schemaToUpdate as OpenAPIV3.ArraySchemaObject).items = value.items;
                } else {
                  // @ts-expect-error we want to delete this property whether or not it exists
                  delete schemaToUpdate.items;
                }

                // try to ensure the enum values are of the correct type when in View mode
                if (previousType !== action.value.type && schemaToUpdate.enum && schemaToUpdate.enum.length > 0) {
                  schemaToUpdate.enum = schemaToUpdate.enum.map(enumValue =>
                    convertToType(enumValue, action.value.type),
                  );
                }
                break;
              default:
            }
          }
          break;
        default:
      }

      // We've successfully updated the schema, so we can return true
      // to stop traversing `propertyIndexMap`.
      return true;
    }

    // If we've reached this point in the logic, it means we haven't made any changes to the schema nor have we
    // reached the last nested level defined in `propertyIndexMap`.
    // If the current property is an object, we need to update `objectToTraverse` to point to the current property
    // so we do the traversal again.
    if (schemaToUpdate && 'properties' in schemaToUpdate && schemaToUpdate.properties) {
      currentNestedLevel += 1;
      objectToTraverse = schemaToUpdate;
    } else if (
      schemaToUpdate &&
      'items' in schemaToUpdate &&
      'type' in schemaToUpdate.items &&
      schemaToUpdate.items.type === 'object'
    ) {
      // if the schemaToUpdate is an array of objects, we need to recurse into the items object
      currentNestedLevel += 1;
      objectToTraverse = schemaToUpdate.items;
    }

    return false;
  });
}

/**
 * Traverses the request schema object and modifies it based on the provided `id`, `state`, `action`, and `mimeType`.
 * Mime type determines which schema object to modify.
 */
function traverseAndModifyRequestSchema(
  id: string,
  state: WritableDraft<APIDesignerStore>,
  action: TraverseAction,
  mimeType?: MIMETypeValue,
  childPolymorphicIndex?: number,
): void {
  const { operation } = getValidatedAPIDesignerState(state);

  const requestBody = operation.requestBody;

  if (!requestBody) {
    // no request body found, so we can't do anything
    return;
  }

  if ('$ref' in requestBody) {
    throw new Error('Cannot add a sub-property to a reference request body');
  }

  if (!mimeType) {
    throw new Error('mimeType is required');
  }

  // by the time we get here, we've done enough type checking to know
  // that currentSchema is a SchemaObject, so we can safely cast it
  let currentSchema = {} as WritableDraft<OpenAPIV3.SchemaObject>;

  if (childPolymorphicIndex !== -1 && childPolymorphicIndex !== undefined) {
    const schema = requestBody.content?.[mimeType]?.schema as WritableDraft<OpenAPIV3.SchemaObject>;
    const polyContent = schema.anyOf || schema.oneOf;
    if (!polyContent) throw new Error('No polymorphic content found');
    currentSchema = polyContent[childPolymorphicIndex] as WritableDraft<OpenAPIV3.SchemaObject>;
    if (!currentSchema) throw new Error('Target schema not found');
  } else {
    currentSchema = requestBody.content?.[mimeType]?.schema as WritableDraft<OpenAPIV3.SchemaObject>;
  }

  if (!currentSchema) {
    return;
  }

  if ('$ref' in currentSchema) {
    throw new Error('Cannot add a sub-property to a reference request body');
  }

  traverseAndModifySchema(action, id, currentSchema);
}

/**
 * Traverses the response schema object and modifies it based on the provided `statusCode`, `id` and `action`.
 * Similar to traverseAndModifyRequestSchema but works specifically with response schemas.
 */
function traverseAndModifyResponseSchema(
  statusCode: string,
  mimeType: MIMETypeValue,
  id: string,
  state: WritableDraft<APIDesignerStore>,
  action: TraverseAction,
  childPolymorphicIndex?: number,
): void {
  const { operation } = getValidatedAPIDesignerState(state);

  const response = operation.responses?.[statusCode];

  if (!response) {
    // no response found, so we can't do anything
    return;
  }

  if ('$ref' in response) {
    throw new Error('Cannot add a sub-property to a reference response');
  }

  // by the time we get here, we've done enough type checking to know
  // that currentSchema is a SchemaObject, so we can safely cast it
  let currentSchema = {} as WritableDraft<OpenAPIV3.SchemaObject>;
  if (childPolymorphicIndex !== -1 && childPolymorphicIndex !== undefined) {
    const schema = response.content?.[mimeType]?.schema as WritableDraft<OpenAPIV3.SchemaObject>;
    const polyContent = schema.anyOf || schema.oneOf;
    if (!polyContent) throw new Error('No polymorphic content found');
    currentSchema = polyContent[childPolymorphicIndex] as WritableDraft<OpenAPIV3.SchemaObject>;
  } else {
    currentSchema = response.content?.[mimeType]?.schema as WritableDraft<OpenAPIV3.SchemaObject>;
  }

  if (!currentSchema) {
    // no JSON response schema found, so we can't do anything
    return;
  }

  if ('$ref' in currentSchema) {
    throw new Error('Cannot add a sub-property to a reference response');
  }

  traverseAndModifySchema(action, id, currentSchema);
}

/**
 * Gets the schema from a parameter, either from the schema field or the content field.
 * For content-based parameters, returns the schema from the first content type.
 * Returns null if no valid schema is found.
 */
export function getParameterSchema(
  parameter: OpenAPIV3.ParameterObject | OpenAPIV3.ReferenceObject,
): OpenAPIV3.SchemaObject | null {
  if ('schema' in parameter && parameter.schema && !('$ref' in parameter.schema)) {
    return parameter.schema as OpenAPIV3.SchemaObject | WritableDraft<OpenAPIV3.SchemaObject>;
  }

  if ('content' in parameter && parameter.content && typeof parameter.content === 'object') {
    const contentKeys = Object.keys(parameter.content);
    const contentType = getParameterContentType(contentKeys);
    if (!contentType) {
      return null;
    }

    const mediaTypeObject = parameter.content[contentType];
    if (
      typeof mediaTypeObject === 'object' &&
      mediaTypeObject &&
      'schema' in mediaTypeObject &&
      mediaTypeObject.schema &&
      !('$ref' in mediaTypeObject.schema)
    ) {
      return mediaTypeObject.schema as OpenAPIV3.SchemaObject | WritableDraft<OpenAPIV3.SchemaObject>;
    }
  }

  return null;
}

/**
 * Traverses the query parameter schema object and modifies it
 * Gets the target parameter object from the parameters array by `paramIndex` and `location`.
 * Supports both schema-based and content-based parameters.
 */
function traverseAndModifyQueryParam(
  location: Exclude<OpenAPIV3.ParameterObject['in'], 'cookie'>,
  paramIndex: number,
  propertyPath: string,
  state: WritableDraft<APIDesignerStore>,
  action: TraverseAction,
): void {
  const { operation } = getValidatedAPIDesignerState(state);

  const parameters = (operation.parameters || []).filter(param => 'in' in param && param.in === location);

  const currentParameter = parameters[paramIndex];

  if (!currentParameter) {
    throw new Error(`Parameter at index ${paramIndex} does not exist`);
  }

  const schema = getParameterSchema(currentParameter);

  if (!schema) {
    throw new Error(`Parameter at index ${paramIndex} does not have a valid schema or contains a reference`);
  }

  traverseAndModifySchema(action, propertyPath, schema);
}

/**
 * Helper function to get request schema with validation
 */
export const getRequestSchema = (state: APIDesignerStore, mimeType: string) => {
  const { operation } = getValidatedAPIDesignerState(state);

  if (!operation.requestBody) throw new Error('Invalid endpoint structure');

  const requestBody = operation.requestBody as RequestBodyObject;
  if (!requestBody.content) throw new Error('Invalid request body structure');

  const mediaContent = requestBody.content[mimeType];
  if (!mediaContent?.schema) throw new Error('Invalid media content');

  return mediaContent.schema as OpenAPIV3.SchemaObject;
};

/**
 * Helper function to get response schema with validation
 */
export const getResponseSchema = (state: APIDesignerStore, statusCode: string, mimeType: string) => {
  const { operation } = getValidatedAPIDesignerState(state);

  if (!operation.responses?.[statusCode]) throw new Error('Invalid endpoint structure');

  const response = operation.responses[statusCode] as ResponseObject;
  if (!response.content) throw new Error('Invalid response structure');

  const mediaContent = response.content[mimeType];
  if (!mediaContent?.schema) throw new Error('Invalid media content');

  return mediaContent.schema as OpenAPIV3.SchemaObject;
};

/**
 * Updates the recently used media types (LIFO queue, max 3 items)
 */
export function updateRecentlyUsedMediaTypes(targetMimeType: string, state: WritableDraft<APIDesignerStore>) {
  if (targetMimeType && !state.recentlyUsedMediaTypes.includes(targetMimeType)) {
    if (state.recentlyUsedMediaTypes.length >= 3) {
      state.recentlyUsedMediaTypes.pop();
    }
    state.recentlyUsedMediaTypes.unshift(targetMimeType);
  } else if (targetMimeType && state.recentlyUsedMediaTypes.includes(targetMimeType)) {
    const index = state.recentlyUsedMediaTypes.indexOf(targetMimeType);
    if (index > 0) {
      state.recentlyUsedMediaTypes.splice(index, 1);
      state.recentlyUsedMediaTypes.unshift(targetMimeType);
    }
  }
}

/**
 * Creates a default empty OpenAPI schema object with object type and empty properties.
 */
export const createEmptySchema = (withProperties = false): OpenAPIV3.SchemaObject => {
  const schema: OpenAPIV3.SchemaObject = {
    type: 'object',
    properties: withProperties ? { name: { type: 'string' } } : {},
  };

  // Only add required if we have properties that are required
  if (withProperties) {
    schema.required = ['name'];
  }

  return schema;
};

/**
 * Checks if a schema represents a primitive type (string, number, boolean, etc.)
 * as opposed to a complex object with properties.
 */
export const isPrimitiveSchema = (schema: OpenAPIV3.SchemaObject): boolean => {
  // If it has properties, it's not primitive
  if (schema.properties && Object.keys(schema.properties).length > 0) {
    return false;
  }

  // If it has additionalProperties, it's not primitive
  if (schema.additionalProperties !== undefined) {
    return false;
  }

  // If it's an array, it's not primitive
  if (schema.type === 'array') {
    return false;
  }

  return PRIMITIVE_TYPES.includes(schema.type as string);
};

/**
 * Checks if an array schema contains primitive type items.
 */
export const isPrimitiveArraySchema = (schema: OpenAPIV3.SchemaObject): boolean => {
  if (schema.type !== 'array') {
    return false;
  }

  if (!schema.items || typeof schema.items !== 'object' || '$ref' in schema.items) {
    return false;
  }

  return isPrimitiveSchema(schema.items);
};

/**
 * Makes sure a content object exists, creating one if needed.
 *
 * Sometimes request/response bodies don't have a `content` property yet, which would
 * cause errors when we try to add media types. This function checks if it exists,
 * creates an empty one if not, and returns it so you can safely use it.
 *
 * @param contentContainer - The request/response body that might need a content object
 * @returns The content object (either the existing one or a fresh empty one)
 *
 * @example
 * ```typescript
 * const requestBody = { description: 'My API endpoint' };
 * const content = ensureContentExists(requestBody);
 * content['application/json'] = { schema: { type: 'object' } };
 * ```
 */
const ensureContentExists = (contentContainer: {
  content?: Record<string, OpenAPIV3.MediaTypeObject>;
}): Record<string, OpenAPIV3.MediaTypeObject> => {
  if (!contentContainer.content) {
    contentContainer.content = {};
  }
  return contentContainer.content;
};

/**
 * Moves content from one media type to another, or creates fresh content if none exists.
 *
 * When users change a media type (like from JSON to XML), we want to preserve their
 * existing schema instead of losing it. This function handles that by moving the content
 * over to the new media type. If there's no existing content to move, it creates a
 * basic empty schema to get them started.
 *
 * @param content - The content object that holds all the different media types
 * @param oldMimeType - What we're changing from (like 'application/json')
 * @param newMimeType - What we're changing to (like 'application/xml')
 *
 * @example
 * ```typescript
 * const content = {
 *   'application/json': {
 *     schema: { type: 'object', properties: { name: { type: 'string' } } }
 *   }
 * };
 *
 * migrateOrCreateMediaTypeContent(content, 'application/json', 'application/xml');
 * // Now the JSON schema is moved to XML, and the JSON entry is removed
 * ```
 */
export const migrateOrCreateMediaTypeContent = (
  content: Record<string, OpenAPIV3.MediaTypeObject>,
  oldMimeType: string,
  newMimeType: string,
): void => {
  if (content[oldMimeType]) {
    content[newMimeType] = content[oldMimeType];
    delete content[oldMimeType];
  } else {
    content[newMimeType] = {
      schema: createEmptySchema(),
    };
  }
};

/**
 * Changes the media type for a request body (like switching from JSON to XML).
 *
 * When someone wants to change their request body from one format to another,
 * this function takes care of all the housekeeping. It makes sure the request body
 * exists, sets up the content structure if needed, and moves any existing schema
 * data over to the new format so nothing gets lost.
 *
 * @param operation - The API operation we're modifying
 * @param args - What we're changing from and to
 * @param args.oldMimeType - Current format (like 'application/json')
 * @param args.newMimeType - New format (like 'application/xml')
 *
 * @example
 * ```typescript
 * // User wants to change from JSON to XML
 * handleRequestMediaTypeChange(operation, {
 *   oldMimeType: 'application/json',
 *   newMimeType: 'application/xml'
 * });
 * ```
 *
 * @see {@link migrateOrCreateMediaTypeContent} for the core migration logic
 * @see {@link ensureContentExists} for content object initialization
 */
export const handleRequestMediaTypeChange = (
  operation: WritableDraft<OpenAPIV3.OperationObject>,
  args: { oldMimeType: string; newMimeType: string },
): void => {
  const { oldMimeType, newMimeType } = args;
  if (!oldMimeType) return;

  if (!operation.requestBody) {
    operation.requestBody = { content: {} };
  }
  const requestBodyContent = ensureContentExists(operation.requestBody as WritableDraft<OpenAPIV3.RequestBodyObject>);
  migrateOrCreateMediaTypeContent(requestBodyContent, oldMimeType, newMimeType);
};

/**
 * Changes the media type for a response body (like switching the 200 response from JSON to XML).
 *
 * Similar to request bodies, but this one works on responses for specific status codes.
 * It finds the response you want to change, makes sure it has the right structure,
 * and moves any existing schema content to the new format. If the response doesn't
 * exist yet, it'll complain loudly so you know something's wrong.
 *
 * @param operation - The API operation we're working with
 * @param args - What we're changing and where
 * @param args.oldMimeType - Current format (like 'application/json')
 * @param args.newMimeType - New format (like 'application/xml')
 * @param args.statusCode - Which response to change (like '200' or '404')
 *
 * @throws {Error} If the status code doesn't exist (we can't change what's not there!)
 *
 * @example
 * ```typescript
 * // Change the 200 response from JSON to XML
 * handleResponseMediaTypeChange(operation, {
 *   oldMimeType: 'application/json',
 *   newMimeType: 'application/xml',
 *   statusCode: '200'
 * });
 * ```
 *
 * @see {@link migrateOrCreateMediaTypeContent} for the core migration logic
 * @see {@link ensureContentExists} for content object initialization
 * @see {@link handleRequestMediaTypeChange} for the request body equivalent
 */
export const handleResponseMediaTypeChange = (
  operation: WritableDraft<OpenAPIV3.OperationObject>,
  args: { oldMimeType: string; newMimeType: string; statusCode: string },
): void => {
  const { oldMimeType, newMimeType, statusCode } = args;
  if (!oldMimeType) return;

  const currentResponse = operation.responses[statusCode];
  if (!currentResponse) {
    throw new Error(`Response with status code ${statusCode} not found`);
  }
  const responseContent = ensureContentExists(currentResponse as WritableDraft<OpenAPIV3.ResponseObject>);
  migrateOrCreateMediaTypeContent(responseContent, oldMimeType, newMimeType);
};

/**
 * Generic helper function to handle schema traversal actions for different targets.
 * Reduces code duplication by centralizing the target-based routing logic.
 *
 * @param params - The parameters for the schema action
 * @param params.target - The target type ('request', 'response', or query param info)
 * @param params.id - The property path to modify
 * @param params.state - The API Designer state
 * @param params.action - The action to perform (add, remove, or update with data)
 * @param params.statusCode - Required for response targets
 * @param params.mimeType - The MIME type to work with
 * @param params.childPolymorphicIndex - Optional polymorphic index for oneOf/anyOf schemas
 */
export const executeSchemaAction = (params: {
  target: SchemaEditorTarget;
  id: string;
  state: WritableDraft<APIDesignerStore>;
  action: TraverseAction;
  statusCode?: string;
  mimeType?: MIMETypeValue;
  childPolymorphicIndex?: number;
}): void => {
  const { target, id, state, action, statusCode, mimeType, childPolymorphicIndex } = params;

  if (target === 'request') {
    traverseAndModifyRequestSchema(id, state, action, mimeType, childPolymorphicIndex);
  } else if (target === 'response' && statusCode) {
    traverseAndModifyResponseSchema(
      statusCode,
      mimeType || MIMEType.ApplicationJSON,
      id,
      state,
      action,
      childPolymorphicIndex,
    );
  } else if (typeof target === 'object' && target.target === 'query') {
    traverseAndModifyQueryParam(target.location, target.index, id, state, action);
  }
};

/**
 * Adds a property to a polymorphic schema (anyOf/oneOf) at the specified index.
 *
 * This function handles adding properties to schemas that use OpenAPI's polymorphic
 * composition (anyOf/oneOf). It locates the specific schema variant at the given
 * index and adds a new string property with a unique name.
 *
 * @param schema - The parent schema containing anyOf or oneOf arrays
 * @param childPolymorphicIndex - The index of the target schema within the polymorphic array
 *
 * @throws {Error} When no polymorphic content (anyOf/oneOf) is found in the schema
 * @throws {Error} When the specified index doesn't exist in the polymorphic array
 *
 * @example
 * ```typescript
 * // For a schema with anyOf: [{ type: 'object', properties: {} }, { type: 'string' }]
 * addPropertyToPolymorphicSchema(schema, 0); // Adds property to the first variant
 * ```
 */
export const addPropertyToPolymorphicSchema = (
  schema: WritableDraft<OpenAPIV3.SchemaObject>,
  childPolymorphicIndex: number,
): void => {
  // Find the polymorphic content (anyOf or oneOf array)
  const polyContent = schema.anyOf || schema.oneOf;
  if (!polyContent) throw new Error('No polymorphic content found');

  // Get the target schema at the specified index
  const targetSchema = polyContent[childPolymorphicIndex] as WritableDraft<OpenAPIV3.SchemaObject>;
  if (!targetSchema) throw new Error('Target schema not found');

  // Add a new property with a unique name
  const existingProperties = targetSchema.properties;
  const newPropertyName = generateUniquePropertyName(existingProperties || {});
  targetSchema.properties = {
    ...existingProperties,
    [newPropertyName]: { type: 'string' },
  };
};

/**
 * Adds a property to a request body schema at the root level.
 *
 * This function handles the complex logic of adding properties to request body schemas,
 * supporting both polymorphic (anyOf/oneOf) and regular schemas. It manages three main scenarios:
 * 1. Adding to existing polymorphic schemas at a specific index
 * 2. Adding to existing regular schemas with properties
 * 3. Creating new schema content when none exists for the specified MIME type
 *
 * @param operation - The OpenAPI operation object containing the request body
 * @param mimeType - The MIME type to target (e.g., 'application/json')
 * @param childPolymorphicIndex - Optional index for polymorphic schemas (anyOf/oneOf)
 *
 * @example
 * ```typescript
 * // Add to regular JSON schema
 * addPropertyToRequestRoot(operation, 'application/json');
 *
 * // Add to first variant of polymorphic schema
 * addPropertyToRequestRoot(operation, 'application/json', 0);
 * ```
 */
export const addPropertyToRequestRoot = (
  operation: WritableDraft<OpenAPIV3.OperationObject>,
  mimeType: MIMETypeValue,
  childPolymorphicIndex?: number,
): void => {
  const currentRequestBody = operation.requestBody;

  if (operation.requestBody && 'content' in operation.requestBody) {
    // Handle existing request body with content
    if (childPolymorphicIndex !== -1 && childPolymorphicIndex !== undefined) {
      // Scenario 1: Add to polymorphic schema at specific index
      const schema = operation.requestBody?.content[mimeType]?.schema as WritableDraft<OpenAPIV3.SchemaObject>;
      addPropertyToPolymorphicSchema(schema, childPolymorphicIndex);
    } else {
      // Scenario 2: Add to regular (non-polymorphic) schema
      const existingProperties = (
        operation.requestBody?.content[mimeType]?.schema as WritableDraft<OpenAPIV3.SchemaObject>
      )?.properties;

      const currentSchema = operation.requestBody?.content[mimeType]?.schema as WritableDraft<OpenAPIV3.SchemaObject>;

      // Handle root-level array types
      if (currentSchema?.type === 'array' && currentSchema.items && 'properties' in currentSchema.items) {
        // For array schemas, add property to items.properties
        const existingItemsProperties = currentSchema.items.properties || {};
        const newPropertyName = generateUniquePropertyName(existingItemsProperties);

        currentSchema.items.properties = {
          ...existingItemsProperties,
          [newPropertyName]: { type: 'string' },
        };
      } else if (existingProperties) {
        // Add property to existing schema with properties
        const newPropertyName = generateUniquePropertyName(existingProperties);

        if (currentSchema && 'properties' in currentSchema) {
          currentSchema.properties = {
            ...currentSchema.properties,
            [newPropertyName]: { type: 'string' },
          };
        }
      } else if (currentRequestBody && 'content' in currentRequestBody) {
        // Scenario 3: Create new schema for this MIME type (media type doesn't exist yet)
        (operation.requestBody as WritableDraft<OpenAPIV3.RequestBodyObject>) = {
          ...currentRequestBody,
          content: {
            ...(currentRequestBody.content as Record<string, OpenAPIV3.MediaTypeObject>),
            [mimeType]: {
              schema: createEmptySchema(true), // Creates { type: 'object', properties: { property1: { type: 'string' } } }
            },
          },
        };
      }
    }
  } else if (currentRequestBody && 'content' in currentRequestBody && currentRequestBody.content) {
    // Create new request body content when requestBody exists but has no content for this operation
    operation.requestBody = {
      ...currentRequestBody,
      content: {
        ...currentRequestBody.content,
        [mimeType]: {
          schema: createEmptySchema(true),
        },
      },
    };
  }
};

/**
 * Adds a property to a response body schema at the root level.
 *
 * This function handles adding properties to response body schemas for a specific HTTP status code.
 * It supports both polymorphic (anyOf/oneOf) and regular schemas, and can create new response
 * content when none exists. The logic is similar to request bodies but operates on the responses
 * object with status code keys.
 *
 * @param operation - The OpenAPI operation object containing the responses
 * @param statusCode - The HTTP status code to target (e.g., '200', '404')
 * @param mimeType - The MIME type to target (e.g., 'application/json')
 * @param childPolymorphicIndex - Optional index for polymorphic schemas (anyOf/oneOf)
 *
 * @example
 * ```typescript
 * // Add to regular 200 JSON response schema
 * addPropertyToResponseRoot(operation, '200', 'application/json');
 *
 * // Add to second variant of polymorphic 201 response
 * addPropertyToResponseRoot(operation, '201', 'application/json', 1);
 * ```
 */
export const addPropertyToResponseRoot = (
  operation: WritableDraft<OpenAPIV3.OperationObject>,
  statusCode: string,
  mimeType: MIMETypeValue,
  childPolymorphicIndex?: number,
): void => {
  if (operation.responses?.[statusCode] && 'content' in operation.responses[statusCode]) {
    // Handle existing response with content
    const existingContent = operation.responses[statusCode]?.content || {};

    if (childPolymorphicIndex !== -1 && childPolymorphicIndex !== undefined) {
      // Scenario 1: Add to polymorphic schema at specific index
      const schema = existingContent[mimeType]?.schema as WritableDraft<OpenAPIV3.SchemaObject>;
      addPropertyToPolymorphicSchema(schema, childPolymorphicIndex);
    } else {
      // Scenario 2: Add to regular (non-polymorphic) schema
      const existingSchema = operation.responses[statusCode]?.content?.[mimeType]
        ?.schema as WritableDraft<OpenAPIV3.SchemaObject>;

      // Handle root-level array types
      if (existingSchema?.type === 'array' && existingSchema.items && 'properties' in existingSchema.items) {
        // For array schemas, add property to items.properties
        const existingItemsProperties = existingSchema.items.properties || {};
        const newPropertyName = generateUniquePropertyName(existingItemsProperties);

        operation.responses[statusCode] = {
          ...operation.responses[statusCode],
          content: {
            ...existingContent,
            [mimeType]: {
              ...operation.responses[statusCode]?.content?.[mimeType],
              schema: {
                ...existingSchema,
                items: {
                  ...existingSchema.items,
                  properties: {
                    ...existingItemsProperties,
                    [newPropertyName]: { type: 'string' },
                  },
                },
              } as WritableDraft<OpenAPIV3.SchemaObject>,
            },
          } as WritableDraft<Record<string, OpenAPIV3.MediaTypeObject>>,
        };
      } else {
        // For object schemas, add property to root properties
        const existingProperties = existingSchema?.properties || {};
        const newPropertyName = generateUniquePropertyName(existingProperties);

        // Reconstruct the response object with the new property
        // Note: We reconstruct the entire object to ensure proper immutability with Immer
        operation.responses[statusCode] = {
          ...operation.responses[statusCode],
          content: {
            ...existingContent,
            [mimeType]: {
              ...operation.responses[statusCode]?.content?.[mimeType],
              schema: {
                ...existingSchema,
                type: existingSchema?.type || 'object',
                properties: {
                  ...existingProperties,
                  [newPropertyName]: { type: 'string' },
                },
                // if we are adding a new property to an empty object, set the required property
                required: Object.keys(existingProperties).length === 0 ? [newPropertyName] : existingSchema?.required,
              } as WritableDraft<OpenAPIV3.SchemaObject>,
            },
          } as WritableDraft<Record<string, OpenAPIV3.MediaTypeObject>>,
        };
      }
    }
  } else {
    // Scenario 3: Create new response content when none exists for this status code
    operation.responses[statusCode] = {
      description: '', // Empty description (will be filled by user)
      content: {
        [mimeType]: {
          schema: createEmptySchema(true), // Creates object with one property
        },
      },
    };
  }
};

/**
 * Adds a property to a query parameter schema.
 *
 * This function handles adding properties to query parameter schemas. Query parameters
 * can be objects or arrays of objects, and this function supports both cases. It validates
 * that the parameter exists, is not a reference, and has a compatible schema type before
 * adding the property.
 *
 * @param state - The API Designer store state containing the operation
 * @param target - The query parameter target with location and index information
 * @param target.location - The parameter location ('query', 'header', 'path', 'cookie')
 * @param target.index - The index of the parameter in the filtered parameters array
 * @param target.target - Always 'query' for this function
 *
 * @throws {Error} When the parameter at the specified index doesn't exist or has no schema
 * @throws {Error} When the parameter is a reference (cannot be modified directly)
 * @throws {Error} When the parameter schema is not an object or array of objects
 *
 * @example
 * ```typescript
 * // Add property to first query parameter
 * addPropertyToQueryParam(state, { target: 'query', location: 'query', index: 0 });
 * ```
 */
export const addPropertyToQueryParam = (
  state: WritableDraft<APIDesignerStore>,
  target: ParamInfo & { target: 'query' },
): void => {
  const { operation } = getValidatedAPIDesignerState(state);

  // Filter parameters by location (query, header, path) and get the target parameter
  const parameters = (operation.parameters || []).filter(param => 'in' in param && param.in === target.location);
  const currentParameter = parameters[target.index];

  if (!currentParameter) {
    throw new Error(`Parameter at index ${target.index} does not exist`);
  }

  const schema = getParameterSchema(currentParameter);

  if (!schema) {
    throw new Error(`Parameter at index ${target.index} does not have a valid schema or contains a reference`);
  }

  if (schema.type === 'object') {
    // Scenario 1: Parameter is an object - add property directly
    const existingProperties = schema.properties || {};
    const newPropertyName = generateUniquePropertyName(existingProperties);
    schema.properties = {
      ...schema.properties,
      [newPropertyName]: { type: 'string' },
    };
  } else if (schema.type === 'array' && schema.items && 'properties' in schema.items) {
    // Scenario 2: Parameter is an array of objects - add property to the items schema
    const existingProperties = schema.items.properties || {};
    const newPropertyName = generateUniquePropertyName(existingProperties);
    schema.items.properties = {
      ...schema.items.properties,
      [newPropertyName]: { type: 'string' },
    };
  } else {
    // Unsupported schema type - cannot add properties to primitives or arrays of primitives
    throw new Error(`Cannot add property to parameter at index ${target.index} because it is not an object or array`);
  }
};

/**
 * Clears all properties from a schema object.
 *
 * Uses `delete` instead of `schema = {}` because Immer's WritableDraft proxies need to track
 * each property deletion individually. Direct assignment can cause state sync issues.
 */
const clearSchema = (schema: WritableDraft<OpenAPIV3.SchemaObject>): void => {
  Object.keys(schema).forEach(key => delete schema[key]);
};

/**
 * Replaces the entire content of a schema with new content.
 */
const replaceSchema = (schema: WritableDraft<OpenAPIV3.SchemaObject>, newContent: OpenAPIV3.SchemaObject): void => {
  clearSchema(schema);
  Object.assign(schema, newContent);
};

/**
 * Creates an array schema with the given items schema.
 */
const createArraySchema = (items: OpenAPIV3.SchemaObject): OpenAPIV3.ArraySchemaObject => ({
  type: 'array',
  items,
});

/**
 * Checks if a schema is polymorphic (has anyOf or oneOf).
 */
export const isPolymorphicSchema = (schema: OpenAPIV3.SchemaObject): boolean => {
  return Boolean(schema.anyOf || schema.oneOf);
};

/**
 * Gets the current root type of a schema (object, array, anyOf, oneOf).
 */
export const getSchemaRootType = (schema: OpenAPIV3.SchemaObject): string => {
  if (schema.anyOf) return 'anyOf';
  if (schema.oneOf) return 'oneOf';
  if (schema.type === 'array') {
    // Check if it's a primitive array
    const arraySchema = schema as OpenAPIV3.ArraySchemaObject;
    if (arraySchema.items && !('$ref' in arraySchema.items)) {
      const itemsSchema = arraySchema.items as OpenAPIV3.SchemaObject;
      if (isPrimitiveSchema(itemsSchema)) {
        return 'array(primitive)';
      }
    }
    return 'array';
  }
  if (schema.type) return schema.type;
  return 'object'; // default fallback
};

/**
 * Transforms a schema to an array type. Handles both array-to-array and object-to-array conversions.
 */
const transformToArray = (schema: WritableDraft<OpenAPIV3.SchemaObject>): void => {
  if (schema.type === 'array') {
    const arrayItems = (schema as OpenAPIV3.ArraySchemaObject).items;
    if (arrayItems && !('$ref' in arrayItems)) {
      const itemsSchema = arrayItems as OpenAPIV3.SchemaObject;
      // If it's a primitive array, convert to array of objects
      if (isPrimitiveSchema(itemsSchema)) {
        const objectArraySchema: OpenAPIV3.ArraySchemaObject = createArraySchema({
          type: 'object',
          properties: {
            value: safeClone(itemsSchema),
          },
        });
        replaceSchema(schema, objectArraySchema);
      }
      // If already an array of objects, no transformation needed
    } else {
      // Array with no items or ref, create empty array of objects
      const objectArraySchema: OpenAPIV3.ArraySchemaObject = createArraySchema(createEmptySchema(true));
      replaceSchema(schema, objectArraySchema);
    }
  } else {
    // Object to array - persist the object as array items
    const newSchema = createArraySchema(safeClone(schema));
    replaceSchema(schema, newSchema);
  }
};

/**
 * Transforms a schema to a primitive array type.
 */
const transformToPrimitiveArray = (schema: WritableDraft<OpenAPIV3.SchemaObject>): void => {
  const primitiveArraySchema: OpenAPIV3.ArraySchemaObject = createArraySchema({ type: 'string' });
  replaceSchema(schema, primitiveArraySchema);
};

/**
 * Transforms an array schema to an object type.
 */
const transformToObject = (schema: WritableDraft<OpenAPIV3.SchemaObject>): void => {
  const arrayItems = (schema as OpenAPIV3.ArraySchemaObject).items;
  if (arrayItems && !('$ref' in arrayItems)) {
    const itemsSchema = arrayItems as OpenAPIV3.SchemaObject;

    // Check if array items are primitive types (string, number, boolean, etc.)
    // If so, wrap them in an object with a property
    if (isPrimitiveSchema(itemsSchema)) {
      const wrappedSchema: OpenAPIV3.SchemaObject = {
        type: 'object',
        properties: {
          value: safeClone(itemsSchema),
        },
      };
      replaceSchema(schema, wrappedSchema);
    } else {
      // Complex object - use directly
      replaceSchema(schema, safeClone(itemsSchema));
    }
  } else {
    replaceSchema(schema, createEmptySchema(true));
  }
};

/**
 * Transforms a polymorphic schema to a single schema type, preserving the first variant.
 */
export const transformPolymorphicToSingle = (
  schema: WritableDraft<OpenAPIV3.SchemaObject>,
  oldType: 'anyOf' | 'oneOf',
  newType: 'array' | 'array(primitive)' | 'object',
): void => {
  const schemaToPersist = schema[oldType]?.[0];

  if (newType === 'object') {
    // Transform to object
    if (!schemaToPersist || '$ref' in schemaToPersist) {
      replaceSchema(schema, createEmptySchema(true));
    } else {
      replaceSchema(schema, safeClone(schemaToPersist as OpenAPIV3.SchemaObject));
    }
  } else if (newType === 'array') {
    // Transform to array
    if (!schemaToPersist || '$ref' in schemaToPersist) {
      replaceSchema(schema, createArraySchema(createEmptySchema(true)));
    } else {
      replaceSchema(schema, createArraySchema(safeClone(schemaToPersist as OpenAPIV3.SchemaObject)));
    }
  } else if (newType === 'array(primitive)') {
    transformToPrimitiveArray(schema);
  }
};

/**
 * Transforms a polymorphic schema to another polymorphic type, preserving all variants.
 */
export const transformPolymorphicToPolymorphic = (
  schema: WritableDraft<OpenAPIV3.SchemaObject>,
  oldType: 'anyOf' | 'oneOf',
  newType: 'anyOf' | 'oneOf',
): void => {
  const polyContent = schema[oldType];
  if (polyContent && Array.isArray(polyContent)) {
    clearSchema(schema);
    schema[newType] = polyContent.map(schemaItem => safeClone(schemaItem));
  }
};

/**
 * Transforms a single schema type to a polymorphic schema.
 */
export const transformSingleToPolymorphic = (
  schema: WritableDraft<OpenAPIV3.SchemaObject>,
  oldType: 'array' | 'array(primitive)' | 'object',
  newType: 'anyOf' | 'oneOf',
): void => {
  if (oldType === 'object') {
    // Object to polymorphic
    const newSchema = [safeClone(schema)];
    clearSchema(schema);
    schema[newType] = newSchema;
  } else if (oldType === 'array' || oldType === 'array(primitive)') {
    // Array to polymorphic - preserve array items as polymorphic schemas
    const arrayItems = (schema as OpenAPIV3.ArraySchemaObject).items;
    const newSchema =
      arrayItems && !('$ref' in arrayItems)
        ? [safeClone(arrayItems as OpenAPIV3.SchemaObject)]
        : [createEmptySchema(true)];
    clearSchema(schema);
    schema[newType] = newSchema;
  }
};

/**
 * Transforms between object and array types.
 */
export const transformObjectArray = (
  schema: WritableDraft<OpenAPIV3.SchemaObject>,
  newType: 'array' | 'array(primitive)' | 'object',
): void => {
  if (newType === 'array') {
    transformToArray(schema);
  } else if (newType === 'array(primitive)') {
    transformToPrimitiveArray(schema);
  } else if (newType === 'object') {
    transformToObject(schema);
  }
};
